// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built flipper. The upstream flipper loads a JSON mesh. This one is an outline of two
// circles joined by tangent lines, extruded upward. The pivot is at the origin and the tip
// points along +x. The group of the item turns it by the flipper angle.

import type { BufferGeometry } from "three";
import type { FlipperSpec } from "../../shared/contracts.ts";
import { extrudeOutline, type Point2 } from "./shared.ts";

/** The two parts of a flipper. */
export interface FlipperGeometry {
  /** The body. It spans the full height. */
  readonly body: BufferGeometry;
  /** The rubber band around the body. */
  readonly rubber: BufferGeometry;
}

/**
 * Outline of a tapered flipper. The base circle is at the origin and the tip circle is at
 * (length, 0). A tangent line joins the two circles on each side.
 */
export function flipperOutline(
  baseRadius: number,
  endRadius: number,
  length: number,
  arc = 12,
): Point2[] {
  const safeLength = Math.max(length, Math.abs(baseRadius - endRadius) + 0.001);
  // The tangent line touches both circles where the outward normal makes the angle phi with +x.
  const phi = Math.acos(Math.min(1, Math.max(-1, (baseRadius - endRadius) / safeLength)));
  const out: Point2[] = [];
  // Base circle: from phi, through pi, to 2 pi - phi.
  for (let i = 0; i <= arc; i++) {
    const t = phi + ((2 * Math.PI - 2 * phi) * i) / arc;
    out.push({ x: baseRadius * Math.cos(t), y: baseRadius * Math.sin(t) });
  }
  // Tip circle: from -phi to phi, through 0.
  for (let i = 0; i <= arc; i++) {
    const t = -phi + (2 * phi * i) / arc;
    out.push({
      x: safeLength + endRadius * Math.cos(t),
      y: endRadius * Math.sin(t),
    });
  }
  return out;
}

/** Build the geometry of a flipper. The base of the body is at y = 0 in the item frame. */
export function generateFlipper(spec: FlipperSpec): FlipperGeometry {
  const body = extrudeOutline(
    flipperOutline(spec.baseRadius, spec.endRadius, spec.flipperRadiusMax),
    spec.height,
  );
  body.name = `flipper.body-${spec.name}`;

  const band = spec.rubberThickness ?? 7;
  const bandHeight = Math.min(spec.rubberHeight ?? 19, spec.height);
  const rubber = extrudeOutline(
    flipperOutline(
      spec.baseRadius + band * 0.5,
      spec.endRadius + band * 0.5,
      spec.flipperRadiusMax,
    ),
    bandHeight,
  );
  rubber.translate(0, (spec.height - bandHeight) / 2, 0);
  rubber.name = `flipper.rubber-${spec.name}`;
  return { body, rubber };
}
